import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Bath, BedDouble, Building2, CalendarClock, CheckCircle2, Heart, Loader2, MapPin, Maximize, Phone, MessageCircle, ShieldCheck, Share2 } from "lucide-react";
import { PropertyCard } from "@/components/docstate/PropertyCard";
import { ViewingForm } from "@/components/docstate/ViewingForm";
import { MortgageCalculator } from "@/components/docstate/MortgageCalculator";
import { reDetail, aed, cap, setJsonLd, clearJsonLd, waShareUrl, dl } from "@/lib/docstate";
import { useShortlist } from "@/context/ShortlistContext";

const Spec = ({ icon: Icon, label, value }) => (
  <div className="ds-card flex items-center gap-3 p-4">
    <Icon className="h-5 w-5 text-[#C9A24B]" />
    <div><p className="text-[11px] uppercase tracking-wide text-[#8A7A6F]">{label}</p><p className="text-sm font-semibold text-white">{value}</p></div>
  </div>
);

export default function DocstateProperty() {
  const { id } = useParams();
  const [p, setP] = useState(undefined); // undefined=loading, null=not found
  const [active, setActive] = useState(0);
  const { has, toggle } = useShortlist();

  useEffect(() => {
    setP(undefined); setActive(0);
    reDetail(id).then(setP).catch(() => setP(null));
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!p) return;
    document.title = `${p.title} | Docstate Properties`;
    const img = (p.photos && p.photos[0]) || p.cover_image;
    setJsonLd("docstate-listing-jsonld", {
      "@context": "https://schema.org",
      "@type": ["Residence", "Product"],
      name: p.title,
      description: p.description || `${cap(p.type || "property")} in ${p.community}, Dubai`,
      image: img ? [img] : undefined,
      numberOfBedrooms: p.bedrooms || undefined,
      numberOfBathroomsTotal: p.bathrooms || undefined,
      floorSize: p.area_sqft ? { "@type": "QuantitativeValue", value: p.area_sqft, unitCode: "FTK" } : undefined,
      address: { "@type": "PostalAddress", addressLocality: p.community || "Dubai", addressRegion: "Dubai", addressCountry: "AE" },
      offers: p.price ? { "@type": "Offer", price: p.price, priceCurrency: "AED", availability: "https://schema.org/InStock", url: window.location.href } : undefined,
    });
    return () => clearJsonLd("docstate-listing-jsonld");
  }, [p]);

  if (p === undefined) return <div className="ds-bg flex min-h-screen items-center justify-center pt-16"><Loader2 className="h-9 w-9 animate-spin text-[#C9A24B]" /></div>;
  if (p === null) return (
    <div className="ds-bg flex min-h-screen flex-col items-center justify-center gap-4 pt-16 text-center">
      <p className="ds-display text-2xl text-[#F3E5AB]">Property not found</p>
      <Link to={dl("/properties")} className="ds-outline-btn px-6 py-3 text-xs">Browse all properties</Link>
    </div>
  );

  const photos = p.photos && p.photos.length ? p.photos : [p.cover_image].filter(Boolean);
  const agent = p.agent;
  const wa = ((agent?.whatsapp || agent?.phone || "") + "").replace(/[^\d]/g, "");
  const mapQ = p.lat && p.lng ? `${p.lat},${p.lng}` : encodeURIComponent(`${p.community}, Dubai`);

  return (
    <div className="ds-bg min-h-screen pt-16" data-testid="docstate-property">
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-8 lg:px-12">
        <Link to={dl("/properties")} className="inline-flex items-center gap-1.5 text-xs text-[#9D8D83] hover:text-[#F3E5AB]"><ArrowLeft className="h-4 w-4" /> Back to results</Link>

        {/* Gallery */}
        <div className="mt-4 grid gap-3 lg:grid-cols-[2fr_1fr]">
          <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-[#C9A24B]/15">
            {photos[active] ? <img src={photos[active]} alt={p.title} className="h-full w-full object-cover" data-testid="gallery-main" /> : <div className="h-full w-full bg-[#121214]" />}
            <span className="absolute left-4 top-4 rounded-full bg-[#0B0B0C]/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#F3E5AB] backdrop-blur-sm">For {cap(p.purpose)}</span>
          </div>
          <div className="grid grid-cols-4 gap-3 lg:grid-cols-2">
            {photos.slice(0, 4).map((ph, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`} className={`aspect-[4/3] overflow-hidden rounded-xl border transition-all ${active === i ? "border-[#C9A24B]" : "border-[#C9A24B]/15 opacity-75 hover:opacity-100"}`}>
                <img src={ph} alt="" className="h-full w-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-10 lg:grid-cols-[1.7fr_1fr]">
          {/* Main */}
          <div>
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/8 pb-6">
              <div>
                <h1 className="ds-display text-2xl font-medium text-white lg:text-3xl">{p.title}</h1>
                <p className="mt-2 flex items-center gap-1.5 text-sm text-[#C9BBB2]"><MapPin className="h-4 w-4 text-[#C9A24B]" /> {p.community}, Dubai</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#8A7A6F]">
                  <span>Ref: {p.reference}</span>
                  {p.permit_number && <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-[#3CB98A]" /> Permit {p.permit_number}</span>}
                </div>
              </div>
              <p className="ds-display text-2xl font-semibold text-[#F3E5AB] lg:text-3xl" data-testid="property-price">{aed(p.price, p.purpose === "rent" ? p.price_period : "")}</p>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button onClick={() => toggle(p)} data-testid="property-save" className="inline-flex items-center gap-2 rounded-full border border-[#C9A24B]/40 px-4 py-2 text-xs font-medium text-[#F3E5AB] transition-colors hover:bg-[#C9A24B]/10">
                <Heart className={`h-4 w-4 ${has(p.id) ? "fill-[#C9A24B] text-[#C9A24B]" : ""}`} /> {has(p.id) ? "Saved to shortlist" : "Save to shortlist"}
              </button>
              <a href={waShareUrl(p)} target="_blank" rel="noreferrer" data-testid="property-share-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-[#C9A24B]/40 px-4 py-2 text-xs font-medium text-[#F3E5AB] transition-colors hover:bg-[#C9A24B]/10">
                <Share2 className="h-4 w-4" /> Share on WhatsApp
              </a>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Spec icon={BedDouble} label="Bedrooms" value={p.type === "studio" ? "Studio" : p.bedrooms} />
              <Spec icon={Bath} label="Bathrooms" value={p.bathrooms} />
              <Spec icon={Maximize} label="Area" value={p.area_sqft ? `${Number(p.area_sqft).toLocaleString()} sqft` : "—"} />
              <Spec icon={Building2} label="Type" value={cap(p.type)} />
            </div>

            {(p.completion_status || p.developer) && (
              <div className="mt-4 flex flex-wrap gap-3">
                {p.completion_status && <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A24B]/25 px-4 py-1.5 text-xs text-[#C9BBB2]"><CalendarClock className="h-3.5 w-3.5 text-[#C9A24B]" /> {cap(p.completion_status)}{p.handover_date ? ` · ${p.handover_date}` : ""}</span>}
                {p.developer && <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A24B]/25 px-4 py-1.5 text-xs text-[#C9BBB2]"><Building2 className="h-3.5 w-3.5 text-[#C9A24B]" /> {p.developer}</span>}
                {p.furnishing && <span className="rounded-full border border-[#C9A24B]/25 px-4 py-1.5 text-xs text-[#C9BBB2]">{p.furnishing}</span>}
              </div>
            )}

            {p.description && (
              <div className="mt-8">
                <h2 className="ds-display text-xl font-semibold text-white">About this property</h2>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#C9BBB2]">{p.description}</p>
              </div>
            )}

            {p.amenities && p.amenities.length > 0 && (
              <div className="mt-8">
                <h2 className="ds-display text-xl font-semibold text-white">Amenities</h2>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {p.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-[#C9BBB2]"><CheckCircle2 className="h-4 w-4 text-[#C9A24B]" /> {a}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Map */}
            <div className="mt-8">
              <h2 className="ds-display text-xl font-semibold text-white">Location</h2>
              <div className="mt-4 aspect-[16/8] overflow-hidden rounded-2xl border border-[#C9A24B]/15">
                <iframe title="map" className="h-full w-full grayscale-[0.2]" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${mapQ}&z=14&output=embed`} />
              </div>
            </div>
          </div>

          {/* Sidebar: agent + viewing form */}
          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            {agent && (
              <div className="ds-card p-5" data-testid="agent-card">
                <div className="flex items-center gap-4">
                  {agent.photo ? <img src={agent.photo} alt={agent.name} className="h-16 w-16 rounded-full border-2 border-[#C9A24B]/40 object-cover" /> : <div className="h-16 w-16 rounded-full bg-[#121214]" />}
                  <div>
                    <p className="ds-display text-base font-semibold text-white">{agent.name}</p>
                    <p className="text-xs text-[#C9A24B]">{agent.title}</p>
                    {agent.rera_brn && <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[#8A7A6F]"><ShieldCheck className="h-3 w-3 text-[#3CB98A]" /> {agent.rera_brn}</p>}
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  {agent.phone && <a href={`tel:${agent.phone}`} className="ds-outline-btn flex-1 px-3 py-2.5 text-xs"><Phone className="h-4 w-4" /> Call</a>}
                  {wa && <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi, I'm interested in ${p.reference} — ${p.title}`)}`} target="_blank" rel="noreferrer" data-testid={`agent-whatsapp-button-${agent.id}`} className="ds-gold-btn flex-1 px-3 py-2.5 text-xs"><MessageCircle className="h-4 w-4" /> WhatsApp</a>}
                </div>
              </div>
            )}
            <ViewingForm listing={p} />
            {p.purpose !== "rent" && p.price > 0 && <MortgageCalculator price={p.price} community={p.community} />}
          </aside>
        </div>

        {/* Similar */}
        {p.similar && p.similar.length > 0 && (
          <div className="border-t border-white/5 py-14">
            <h2 className="ds-display text-2xl font-medium text-white">Similar in {p.community}</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
              {p.similar.map((l, i) => <PropertyCard key={l.id} listing={l} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
